/**
 * Player carrello: decodifica l'intero file e lo riproduce per intero (Web Audio).
 * @type {Map<string, { state: { volume: number }, warm: () => void, play: () => Promise<void>, ready: () => Promise<void> }>}
 */
const fabCartSoundPlayers = new Map();

/**
 * @param {HTMLAudioElement} audio
 * @returns {Promise<void>}
 */
function waitForFabCartAudioReady(audio) {
  return new Promise((resolve) => {
    if (audio.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
      resolve();
      return;
    }

    const finish = () => {
      audio.removeEventListener('canplaythrough', finish);
      audio.removeEventListener('error', finish);
      resolve();
    };

    audio.addEventListener('canplaythrough', finish, { once: true });
    audio.addEventListener('error', finish, { once: true });
    audio.load();
  });
}

/**
 * @param {string} soundUrl
 * @param {number} volume
 */
function getFabCartSoundPlayer(soundUrl, volume) {
  const cached = fabCartSoundPlayers.get(soundUrl);
  if (cached) {
    cached.state.volume = volume;
    return cached;
  }

  const state = { volume };

  /** @type {AudioContext | null} */
  let audioContext = null;
  /** @type {AudioBuffer | null} */
  let audioBuffer = null;
  /** @type {Promise<void> | null} */
  let decodePromise = null;
  /** @type {HTMLAudioElement | null} */
  let htmlFallback = null;

  const ensureContext = () => {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!audioContext) audioContext = new Ctx();
    return audioContext;
  };

  const decodeSound = () => {
    if (decodePromise) return decodePromise;

    decodePromise = (async () => {
      const context = ensureContext();
      if (!context) return;

      try {
        const response = await fetch(soundUrl);
        if (!response.ok) return;

        const arrayBuffer = await response.arrayBuffer();
        audioBuffer = await context.decodeAudioData(arrayBuffer.slice(0));
      } catch {
        audioBuffer = null;
      }
    })();

    return decodePromise;
  };

  const warm = () => {
    if (!htmlFallback) {
      htmlFallback = new Audio(soundUrl);
      htmlFallback.preload = 'metadata';
    }
  };

  const ready = async () => {
    warm();
    await decodeSound();
    if (htmlFallback) {
      await waitForFabCartAudioReady(htmlFallback);
    }
  };

  const resumeContext = async () => {
    const context = ensureContext();
    if (!context || context.state !== 'suspended') return;
    try {
      await context.resume();
    } catch {
      // ignore
    }
  };

  const playWithWebAudio = () => {
    if (!audioBuffer || !audioContext) return false;

    try {
      const source = audioContext.createBufferSource();
      const gain = audioContext.createGain();
      source.buffer = audioBuffer;
      gain.gain.value = state.volume;
      source.connect(gain);
      gain.connect(audioContext.destination);
      source.start(0);
      return true;
    } catch {
      return false;
    }
  };

  const playWithHtmlFallback = async () => {
    if (!htmlFallback) {
      htmlFallback = new Audio(soundUrl);
      htmlFallback.preload = 'auto';
    }

    htmlFallback.volume = state.volume;
    await waitForFabCartAudioReady(htmlFallback);

    try {
      htmlFallback.pause();
      htmlFallback.currentTime = 0;
      await htmlFallback.play();
    } catch {
      // ignore
    }
  };

  const play = async () => {
    await resumeContext();
    await ready();

    if (playWithWebAudio()) return;

    await resumeContext();
    if (playWithWebAudio()) return;

    await playWithHtmlFallback();
  };

  const player = { state, warm, ready, play };
  fabCartSoundPlayers.set(soundUrl, player);

  return player;
}

/**
 * @param {HTMLElement} root
 * @returns {HTMLElement | null}
 */
function findFabAddToCartButton(root, target) {
  if (!(target instanceof Element)) return null;

  const button = target.closest(
    '.add-to-cart-button, add-to-cart-component button[type="submit"][name="add"]'
  );

  if (!button || !(button instanceof HTMLButtonElement) || button.disabled) return null;
  if (!root.contains(button)) return null;

  return button;
}

/**
 * @param {HTMLElement} section
 * @returns {number}
 */
function getFabCartSoundVolume(section) {
  const volume = Number.parseFloat(section.dataset.hoverSoundVolume ?? '1');
  return Number.isNaN(volume) ? 1 : Math.min(1, Math.max(0, volume));
}

function warmAllFabCartSounds() {
  document.querySelectorAll('[data-hover-sound-url]').forEach((section) => {
    if (!(section instanceof HTMLElement)) return;
    const soundUrl = section.dataset.hoverSoundUrl;
    if (!soundUrl) return;
    getFabCartSoundPlayer(soundUrl, getFabCartSoundVolume(section)).warm();
  });
}

/**
 * Riproduce un suono al click su "Aggiungi al carrello".
 */
function initFabProductCartSound(section) {
  if (!section || section.dataset.cartSoundInit === 'true') return;

  const soundUrl = section.dataset.hoverSoundUrl;
  if (!soundUrl) return;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  section.dataset.cartSoundInit = 'true';

  const player = getFabCartSoundPlayer(soundUrl, getFabCartSoundVolume(section));
  void player.ready();

  section.addEventListener(
    'pointerenter',
    (event) => {
      if (findFabAddToCartButton(section, event.target)) {
        void player.ready();
      }
    },
    true
  );

  section.addEventListener(
    'click',
    (event) => {
      const button = findFabAddToCartButton(section, event.target);
      if (!button) return;

      void player.play();
    },
    true
  );
}

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeFabColor(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

/**
 * @param {string | undefined} colorsAttr
 * @returns {string[] | null}
 */
function parseFabColorKeys(colorsAttr) {
  if (!colorsAttr || colorsAttr === '*') return null;
  return colorsAttr
    .split(',')
    .map((part) => normalizeFabColor(part))
    .filter(Boolean);
}

/**
 * @param {string | undefined} colorsAttr
 * @param {string | undefined} selectedColor
 * @returns {boolean}
 */
function fabColorMatches(colorsAttr, selectedColor) {
  const allowed = parseFabColorKeys(colorsAttr);
  if (allowed === null) return true;

  const selected = normalizeFabColor(selectedColor);
  if (!selected) return true;

  return allowed.some((color) => color === selected);
}

/**
 * @param {HTMLElement} section
 * @param {object | null | undefined} variant
 * @returns {string}
 */
function getFabVariantColor(section, variant) {
  const picker = section.querySelector('[data-fab-disegno-picker]');
  const position = Number(picker?.dataset.fabColorOptionPosition || 0);

  if (variant && position > 0) {
    const optionValue = variant[`option${position}`];
    if (optionValue) return String(optionValue);
  }

  return picker?.dataset.fabInitialColor || '';
}


/**
 * @param {HTMLElement} section
 * @param {string} colorValue
 */
function scheduleFabDisegnoColorFilter(section, colorValue) {
  const frameKey = '_fabDisegnoFilterFrame';
  const existing = section[frameKey];
  if (existing) cancelAnimationFrame(existing);

  section[frameKey] = requestAnimationFrame(() => {
    section[frameKey] = 0;
    applyFabDisegnoColorFilter(section, colorValue);
  });
}

/**
 * @param {HTMLElement} section
 * @param {string} colorValue
 * @returns {boolean}
 */
function applyFabDisegnoColorFilter(section, colorValue) {
  const picker = section.querySelector('[data-fab-disegno-picker]');
  if (!picker) return false;

  /** @type {HTMLButtonElement | null} */
  let firstVisible = null;

  picker.querySelectorAll('.fab-product-disegno__btn').forEach((btn) => {
    const visible = fabColorMatches(btn.dataset.fabDisegnoColors, colorValue);
    btn.hidden = !visible;
    btn.disabled = !visible;
    btn.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (visible && !firstVisible) firstVisible = btn;
  });

  section.querySelectorAll('.fab-product-page__media-item--disegno').forEach((item) => {
    const visible = fabColorMatches(item.dataset.fabDisegnoColors, colorValue);
    item.dataset.fabDisegnoVisible = visible ? 'true' : 'false';
  });

  const emptyEl = picker.querySelector('.fab-product-disegno__empty');
  if (emptyEl) emptyEl.hidden = Boolean(firstVisible);

  const input = picker.querySelector('.fab-product-disegno__value-input');
  const isRequired = picker.dataset.fabDisegnoRequired === 'true';
  if (input) input.required = isRequired && Boolean(firstVisible);

  if (firstVisible) {
    const selected = picker.querySelector('.fab-product-disegno__btn.is-selected:not([hidden])');
    if (
      selected instanceof HTMLButtonElement &&
      !selected.disabled &&
      fabColorMatches(selected.dataset.fabDisegnoColors, colorValue)
    ) {
      const mediaId = selected.dataset.fabDisegnoMediaId;
      if (mediaId && section.dataset.fabActiveMediaId !== String(mediaId)) {
        selectFabDisegno(section, selected);
      }
      return true;
    }

    selectFabDisegno(section, firstVisible);
    return true;
  }

  clearFabDisegnoPickerUi(section);
  if (input) input.value = '';

  section.dataset.fabMediaSource = 'variant';
  const variantMediaId = section.dataset.fabLastVariantMediaId;
  if (variantMediaId) {
    setFabActiveMedia(section, variantMediaId, 'variant');
  }

  return false;
}

/**
 * Mostra un media (immagine o video) nel frame principale.
 * @param {HTMLElement} section
 * @param {string|number} mediaId
 * @param {'disegno' | 'gallery' | 'variant' | 'manual'} [source]
 * @returns {boolean}
 */
function setFabActiveMedia(section, mediaId, source = 'manual') {
  if (!section || mediaId == null) return false;

  const targetId = String(mediaId);
  const mediaItems = section.querySelectorAll('.fab-product-page__media-item');
  if (!mediaItems.length) return false;

  let matched = false;

  mediaItems.forEach((item) => {
    const isActive = item.dataset.mediaId === targetId;
    if (isActive) matched = true;
    item.classList.toggle('is-active', isActive);

    if (isActive) {
      item.removeAttribute('hidden');
      item.style.removeProperty('display');
    } else {
      item.setAttribute('hidden', '');
      item.style.removeProperty('display');
      item.querySelectorAll('video').forEach((video) => {
        video.pause();
      });
    }
  });

  if (!matched) return false;

  section.dataset.fabActiveMediaId = targetId;
  section.dataset.fabMediaSource = source;

  const gallery = section.querySelector('.fab-product-page__gallery');
  if (gallery) {
    gallery.querySelectorAll('.fab-product-page__gallery-thumb').forEach((thumb) => {
      const isActive = thumb.dataset.mediaId === targetId;
      thumb.classList.toggle('is-active', isActive);
      thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  if (source === 'disegno') {
    syncFabDisegnoPickerUi(section, targetId);
  } else if (source === 'gallery' || source === 'variant') {
    clearFabDisegnoPickerUi(section);
  }

  return true;
}

function showFabProductMedia(section, mediaId) {
  return setFabActiveMedia(section, mediaId, 'gallery');
}

/**
 * @param {HTMLElement} section
 * @param {string} mediaId
 */
function syncFabDisegnoPickerUi(section, mediaId) {
  const picker = section.querySelector('[data-fab-disegno-picker]');
  if (!picker) return;

  picker.querySelectorAll('.fab-product-disegno__btn').forEach((btn) => {
    const selected = btn.dataset.fabDisegnoMediaId === mediaId;
    btn.classList.toggle('is-selected', selected);
    btn.setAttribute('aria-checked', selected ? 'true' : 'false');

    if (selected) {
      const input = picker.querySelector('.fab-product-disegno__value-input');
      if (input && btn.dataset.fabDisegnoValue) {
        input.value = btn.dataset.fabDisegnoValue;
      }
    }
  });
}

/** @param {HTMLElement} section */
function clearFabDisegnoPickerUi(section) {
  const picker = section.querySelector('[data-fab-disegno-picker]');
  if (!picker) return;

  picker.querySelectorAll('.fab-product-disegno__btn').forEach((btn) => {
    btn.classList.remove('is-selected');
    btn.setAttribute('aria-checked', 'false');
  });
}

/**
 * @param {HTMLElement} section
 * @param {HTMLButtonElement} btn
 */
function selectFabDisegno(section, btn) {
  if (btn.hidden || btn.disabled) return;

  const picker = btn.closest('[data-fab-disegno-picker]');
  if (!picker) return;

  const value = btn.dataset.fabDisegnoValue;
  const input = picker.querySelector('.fab-product-disegno__value-input');
  if (input && value) input.value = value;

  picker.querySelectorAll('.fab-product-disegno__btn').forEach((option) => {
    const selected = option === btn;
    option.classList.toggle('is-selected', selected);
    option.setAttribute('aria-checked', selected ? 'true' : 'false');
  });

  const mediaId = btn.dataset.fabDisegnoMediaId;
  if (mediaId) {
    const mediaItem = section.querySelector(`.fab-product-page__media-item--disegno[data-media-id="${mediaId}"]`);
    if (mediaItem?.dataset.fabDisegnoVisible === 'false') return;
    setFabActiveMedia(section, mediaId, 'disegno');
  } else {
    section.dataset.fabMediaSource = 'disegno';
  }
}

/**
 * Seleziona il colore nel variant picker quando si clicca una miniatura collegata a una variante.
 * @param {HTMLElement} section
 * @param {HTMLElement} thumb
 * @returns {boolean}
 */
function selectFabVariantColorFromGallery(section, thumb) {
  const optionValueId = thumb.dataset.fabColorOptionValueId;
  if (!optionValueId) return false;

  const variantPicker = section.querySelector('variant-picker');
  if (!variantPicker) return false;

  const input = variantPicker.querySelector(`[data-option-value-id="${optionValueId}"]`);
  if (!input || input.disabled) return false;

  if (input instanceof HTMLInputElement) {
    if (input.checked) return false;
    section.dataset.fabGalleryVariantSync = 'true';
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  if (input instanceof HTMLOptionElement && input.parentElement instanceof HTMLSelectElement) {
    if (input.selected) return false;
    section.dataset.fabGalleryVariantSync = 'true';
    input.parentElement.value = input.value;
    input.parentElement.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  return false;
}

/**
 * Miniature galleria + switch immagine/video.
 */
function initFabProductGallery(section) {
  if (!section || section.dataset.galleryInit === 'true') return;

  const gallery = section.querySelector('.fab-product-page__gallery');
  const mediaItems = section.querySelectorAll('.fab-product-page__media-item');
  if (!mediaItems.length) return;

  section.dataset.galleryInit = 'true';

  if (gallery) {
    gallery.addEventListener('click', (event) => {
      const thumb = event.target.closest('.fab-product-page__gallery-thumb');
      if (!thumb || thumb.disabled) return;

      setFabActiveMedia(section, thumb.dataset.mediaId, 'gallery');
      selectFabVariantColorFromGallery(section, thumb);
    });
  }

  section.addEventListener('variant:update', (event) => {
    const inventory = section.querySelector('product-inventory.fab-product-inventory');
    const newInventory = event.detail?.data?.html?.querySelector('product-inventory.fab-product-inventory');
    if (inventory && newInventory) {
      inventory.className = newInventory.className;
    }

    const fromGallery = section.dataset.fabGalleryVariantSync === 'true';
    if (fromGallery) delete section.dataset.fabGalleryVariantSync;

    const variant = event.detail?.resource;
    const mediaId = variant?.featured_media?.id;

    if (mediaId) {
      section.dataset.fabLastVariantMediaId = String(mediaId);
    }

    const picker = section.querySelector('[data-fab-disegno-picker]');
    if (picker) {
      scheduleFabDisegnoColorFilter(section, getFabVariantColor(section, variant));
      if (!fromGallery) return;
    }

    if (fromGallery) return;

    if (section.dataset.fabMediaSource === 'disegno') return;
    if (!mediaId) return;

    setFabActiveMedia(section, mediaId, 'variant');
  });
}

function initFabProductDisegno(section) {
  if (!section || section.dataset.disegnoInit === 'true') return;

  const picker = section.querySelector('[data-fab-disegno-picker]');
  if (!picker) return;

  section.dataset.disegnoInit = 'true';

  picker.addEventListener('click', (event) => {
    const btn = event.target.closest('.fab-product-disegno__btn');
    if (!btn || !picker.contains(btn) || btn.hidden || btn.disabled) return;

    selectFabDisegno(section, btn);
  });

  picker.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const btn = event.target.closest('.fab-product-disegno__btn');
    if (!btn || !picker.contains(btn) || btn.hidden || btn.disabled) return;

    event.preventDefault();
    selectFabDisegno(section, btn);
  });

  scheduleFabDisegnoColorFilter(section, picker.dataset.fabInitialColor || getFabVariantColor(section, null));
}

function initFabProductVideosalutoBrief(section) {
  const wrapper = section.querySelector('[data-fab-videosaluto-brief]');
  if (!wrapper) return;

  const input = wrapper.querySelector('[data-fab-videosaluto-brief-input]');
  const counter = wrapper.querySelector('[data-fab-videosaluto-char-count]');
  if (!input || !(input instanceof HTMLTextAreaElement) || !counter) return;

  const validationMessage =
    wrapper.dataset.requiredMessage?.trim() ||
    'Scrivi il messaggio personalizzato prima di aggiungere al carrello.';

  const form = section.querySelector('form[data-type="add-to-cart-form"]');
  const acceleratedCheckout = section.querySelector('.accelerated-checkout-block');

  const isBriefValid = () => input.value.trim().length > 0;

  const validateBrief = () => {
    if (!isBriefValid()) {
      input.setCustomValidity(validationMessage);
      return false;
    }

    input.setCustomValidity('');
    return true;
  };

  const getAddToCartButtons = () =>
    Array.from(section.querySelectorAll('.add-to-cart-button[type="submit"][name="add"]')).filter(
      (btn) => btn instanceof HTMLButtonElement
    );

  const isSoldOutButton = (/** @type {HTMLButtonElement} */ btn) =>
    btn.disabled && btn.dataset.fabVideosalutoBlocked !== 'true';

  const syncAddToCartState = () => {
    validateBrief();
    const valid = isBriefValid();

    getAddToCartButtons().forEach((btn) => {
      if (isSoldOutButton(btn)) return;

      if (!valid) {
        btn.disabled = true;
        btn.dataset.fabVideosalutoBlocked = 'true';
        return;
      }

      btn.disabled = false;
      delete btn.dataset.fabVideosalutoBlocked;
    });

    if (acceleratedCheckout instanceof HTMLElement) {
      acceleratedCheckout.toggleAttribute('hidden', !valid);
    }
  };

  const updateCount = () => {
    counter.textContent = String(input.value.length);
  };

  const onInput = () => {
    updateCount();
    syncAddToCartState();
  };

  if (!wrapper.dataset.fabVideosalutoBriefBound) {
    wrapper.dataset.fabVideosalutoBriefBound = 'true';
    input.addEventListener('input', onInput);
    input.addEventListener('blur', validateBrief);

    if (form) {
      section.addEventListener(
        'click',
        (event) => {
          const button = findFabAddToCartButton(section, event.target);
          if (!button) return;

          if (!validateBrief()) {
            event.preventDefault();
            event.stopPropagation();
            input.reportValidity();
            syncAddToCartState();
          }
        },
        true
      );

      form.addEventListener(
        'submit',
        (event) => {
          if (!validateBrief()) {
            event.preventDefault();
            event.stopPropagation();
            input.reportValidity();
          }
        },
        true
      );
    }

    section.addEventListener('variant:update', () => {
      requestAnimationFrame(syncAddToCartState);
    });
  }

  updateCount();
  syncAddToCartState();
  delete section.dataset.fabVideosalutoPending;
}

function initFabProductPage(section) {
  const activeProductMedia = section.querySelector(
    '.fab-product-page__media-item.is-active:not(.fab-product-page__media-item--disegno)'
  );
  if (activeProductMedia?.dataset.mediaId) {
    section.dataset.fabLastVariantMediaId = activeProductMedia.dataset.mediaId;
  }

  initFabProductCartSound(section);
  initFabProductGallery(section);
  initFabProductDisegno(section);
  initFabProductVideosalutoBrief(section);
}

function initAllFabProductPages(root = document) {
  root.querySelectorAll('.fab-product-page').forEach(initFabProductPage);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllFabProductPages());
} else {
  initAllFabProductPages();
}

document.addEventListener(
  'pointerdown',
  () => {
    if (!document.querySelector('[data-hover-sound-url]')) return;
    warmAllFabCartSounds();
  },
  { once: true, capture: true }
);

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (!target) return;

  if (target.matches?.('.fab-product-page')) {
    initFabProductPage(target);
    return;
  }

  initAllFabProductPages(target);
});

/** Usato dalla modale quick-add dopo il caricamento prodotto */
window.FabProductPage = {
  init: initFabProductPage,
  showMedia: showFabProductMedia,
};
